import { styled } from "@linaria/react"
import { useQuery } from "@tanstack/react-query"
import { useContext } from "react"

import Skeleton from "./Skeleton"
import { ConfigContext } from "../contexts/config"

type TokenLogoProps = {
  tx?: string | undefined
  alt: string
  /**
   * @default "medium"
   */
  size?: "small" | "medium"
}

type useTokenLogoProps = {
  tx?: string
}

export function useTokenLogo({ tx }: useTokenLogoProps) {
  const [config] = useContext(ConfigContext)

  return useQuery({
    queryKey: ["token-logo", tx],
    queryFn: async () => {
      try {
        const res = await (await fetch(`${config.GATEWAY_URL}/${tx}`)).blob()
        const imageUrl = URL.createObjectURL(res)

        return imageUrl
      } catch (error) {
        console.error("Failed to fetch token logo", error)
        return undefined
      }
    },
    enabled: !!tx,
  })
}

const SIZES = {
  small: "1.125rem",
  medium: "1.625rem",
}

export default function TokenLogo({ tx, alt, size = "medium" }: TokenLogoProps) {
  const { data: imageUrl, isLoading, isError } = useTokenLogo({ tx })

  const imageSize = SIZES[size]

  if (isLoading) {
    return <Skeleton width={imageSize} height={imageSize} borderRadius="100%" />
  }

  if (isError || !imageUrl) {
    return (
      <TokenImagePlaceholder
        style={{
          width: imageSize,
          height: imageSize,
          minWidth: imageSize,
          minHeight: imageSize,
          fontSize: `calc(${imageSize} / 1.7)`,
        }}
      >
        {alt?.substring(0, 1)}
      </TokenImagePlaceholder>
    )
  }

  return (
    <TokenImage
      src={imageUrl}
      style={{
        width: imageSize,
        height: imageSize,
      }}
      alt={alt || "Token logo"}
    />
  )
}

export const TokenImage = styled.img`
  user-select: none;
  object-fit: contain;
`

export const TokenImagePlaceholder = styled.div`
  user-select: none;
  border-radius: 50%;
  color: #fff;
  background-color: #404040;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;
`
